import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ShoppingBag, ExternalLink, Sparkles, Package, Shirt, Frame, Coffee, Gift, Layers, Cog } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useBrandWord } from '../hooks/useBrandWord';

// ─── Categories & their icons ─────────────────────────────────────────────
// Kept lightweight so we can iterate on visuals without touching the API.
const CATEGORIES = [
  { id: 'all',         label: 'All Hot Merch', icon: Sparkles },
  { id: '3d-print',    label: '3D Printed',    icon: Cog },
  { id: 'plush',       label: 'Plushies',      icon: Package },
  { id: 'apparel',     label: 'Apparel',       icon: Shirt },
  { id: 'poster',      label: 'Posters',       icon: Frame },
  { id: 'accessory',   label: 'Accessories',   icon: Coffee },
  { id: 'collectible', label: 'Collectibles',  icon: Gift },
  { id: 'digital',     label: 'Digital',       icon: Layers },
];

function fmtPrice(cents, currency = 'USD') {
  if (cents == null) return null;
  const dollars = cents / 100;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(dollars);
  } catch {
    return `$${dollars.toFixed(2)}`;
  }
}

export default function MerchPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [active, setActive] = useState('all');
  const [comingSoon, setComingSoon] = useState(false);
  const brandWord = useBrandWord();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [list, settings] = await Promise.all([
          api.listMerch({ limit: 120 }),
          api.siteSettings().catch(() => ({ merch_coming_soon: false })),
        ]);
        if (alive) {
          setProducts(list);
          setComingSoon(!!settings?.merch_coming_soon);
        }
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load merch');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const list = active === 'all'
      ? products
      : products.filter((p) => p.category === active);
    // Pin 3D-printed sprites to the top of the "All" view — they're our own
    // handmade product, everything else is affiliate curation.
    if (active !== 'all') return list;
    const priority = (p) => (p.category === '3d-print' ? 0 : 1);
    return [...list].sort((a, b) => priority(a) - priority(b));
  }, [products, active]);

  const featured = useMemo(() => {
    // Pin 3D-printed sprites to the front of the Featured strip — everything
    // else is affiliate curation and should sit behind our own products.
    const list = products.filter((p) => p.featured);
    const priority = (p) => (p.category === '3d-print' ? 0 : 1);
    return [...list].sort((a, b) => priority(a) - priority(b)).slice(0, 4);
  }, [products]);

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(ellipse at 30% 0%, #ff2bd6 0%, #7b2bff 35%, #14002e 90%)',
        color: '#fff',
      }}
      data-testid="merch-page"
    >
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10 pb-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 border-2 border-white/30 px-3 py-2 font-black text-white text-xs sm:text-sm backdrop-blur"
          data-testid="merch-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Vault
        </Link>
        <div className="flex items-center gap-2 rounded-full bg-[#ffd900] text-[#0a0018] px-4 py-1.5 border-2 border-[#0a0018] shadow-[0_4px_0_#0a0018] font-black text-xs sm:text-sm tracking-wider">
          <ShoppingBag className="h-4 w-4" />
          HOT MERCH FOR SALE
        </div>
      </header>

      {/* Hero title */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-6 sm:pb-10 text-center">
        <h1
          key={brandWord}
          className="font-black uppercase tracking-tight leading-[0.9] text-[#ffd900]"
          style={{
            fontSize: 'clamp(48px, 8vw, 108px)',
            textShadow: '0 6px 0 #14002e, 0 12px 24px rgba(0,0,0,0.55)',
            WebkitTextStroke: '3px #14002e',
            animation: 'brandFade 400ms ease-out',
          }}
          data-testid="merch-brand-word"
        >
          {brandWord}
        </h1>
        <div
          className="inline-block mt-2 px-6 py-2 rounded-full border-4 border-[#ffd900] bg-[#14002e] font-black uppercase tracking-[6px] text-[#ffd900]"
          style={{ fontSize: 'clamp(18px, 3.2vw, 36px)' }}
        >
          MERCH SHOP
        </div>
        <p className="mt-4 text-white/80 font-bold max-w-2xl mx-auto text-sm sm:text-base">
          Hand-picked Fortnite-themed gear, exclusive 3D-printed sprites, and everything
          your squad needs to rep the Vault.
        </p>
      </section>

      {/* Featured strip */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-1 flex-1 bg-gradient-to-r from-transparent via-[#ffd900] to-transparent" />
            <div className="font-black uppercase tracking-[6px] text-[#ffd900] text-xs sm:text-sm">
              🔥 Featured Drops
            </div>
            <div className="h-1 flex-1 bg-gradient-to-r from-transparent via-[#ffd900] to-transparent" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((p) => (
              <ProductCard key={`f-${p.id}`} product={p} accent="gold" />
            ))}
          </div>
        </section>
      )}

      {/* Category pill nav */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-6">
        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`merch-cat-${c.id}`}
                className={`flex-shrink-0 flex items-center gap-2 rounded-full border-2 px-4 py-2 font-black uppercase tracking-widest text-[11px] sm:text-xs transition-all ${
                  isActive
                    ? 'bg-[#ffd900] text-[#0a0018] border-[#0a0018] shadow-[0_4px_0_#0a0018]'
                    : 'bg-white/10 text-white border-white/30 hover:bg-white/20'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {c.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-16">
        {loading ? (
          <div className="py-24 text-center text-white/80 font-black uppercase tracking-widest">
            Loading merch…
          </div>
        ) : error ? (
          <div className="py-12 text-center text-[#ff2bd6] font-black">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState active={active} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* Admin-controlled "Coming Soon" splash — hides all product listings
          behind a full-page overlay when the site_settings flag is on. */}
      {comingSoon && <MerchComingSoonOverlay />}
    </div>
  );
}

// ─── Coming Soon flash banner ─────────────────────────────────────────────
// Diagonal ribbon that slides across the whole screen when the admin toggle
// is on, holds for a few seconds, slides off, waits ~15s, and replays.
// The merch page stays fully visible + interactive under the banner. During
// the hold phase the text scrolls continuously left→right for extra emphasis.
const CYCLE_MS = 15_000;   // full replay cadence
const IN_MS    = 900;
const HOLD_MS  = 2400;
const OUT_MS   = 1100;

function MarqueeText() {
  return (
    <span
      className="merch-coming-soon-text"
      style={{
        fontFamily: "'Burbank Big Condensed Black','Titan One','Impact',sans-serif",
        fontWeight: 900,
        fontSize: 'clamp(38px, 8vw, 96px)',
        letterSpacing: '10px',
        color: '#0a0018',
        textShadow: '0 3px 0 #ffffff, 0 6px 0 #14002e, 0 12px 24px rgba(0,0,0,0.5)',
        WebkitTextStroke: '2px #0a0018',
        whiteSpace: 'nowrap',
        display: 'inline-block',
        paddingRight: '80px',
      }}
    >
      🚧 COMING SOON · COMING SOON · COMING SOON · COMING SOON 🚧
    </span>
  );
}

function MerchComingSoonOverlay() {
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState('in'); // 'in' → 'hold' → 'out' → hidden

  useEffect(() => {
    let cancelled = false;
    let interval = null;
    const timers = [];

    const runOne = () => {
      if (cancelled) return;
      setPhase('in');
      setVisible(true);
      timers.push(setTimeout(() => !cancelled && setPhase('hold'), IN_MS));
      timers.push(setTimeout(() => !cancelled && setPhase('out'),  IN_MS + HOLD_MS));
      timers.push(setTimeout(() => !cancelled && setVisible(false), IN_MS + HOLD_MS + OUT_MS));
    };

    // Fire the first flash immediately — a tiny 80ms delay only exists so
    // React has time to paint the merch page under the banner. Then replay
    // every CYCLE_MS on a persistent interval so recurring visitors keep
    // getting the reminder while they scroll.
    timers.push(setTimeout(() => {
      runOne();
      interval = setInterval(runOne, CYCLE_MS);
    }, 80));

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (interval) clearInterval(interval);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="merch-coming-soon-overlay"
      className="pointer-events-none fixed inset-0 z-50 overflow-hidden"
      aria-hidden="true"
    >
      <div
        className={`merch-coming-soon-ribbon ${phase}`}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-18deg)',
          width: '160vw',
          background:
            'linear-gradient(90deg, #ff2bd6 0%, #ffd900 50%, #ff2bd6 100%)',
          borderTop: '5px solid #0a0018',
          borderBottom: '5px solid #0a0018',
          boxShadow: '0 12px 40px rgba(0,0,0,0.55), 0 0 40px rgba(255,43,214,0.5)',
          padding: '22px 0',
          overflow: 'hidden',
        }}
      >
        {/* Marquee track — the text-content is doubled so the loop looks
            seamless when the first copy scrolls fully off the left edge. */}
        <div className="merch-coming-soon-track" aria-hidden="true">
          <MarqueeText />
          <MarqueeText />
        </div>
      </div>
    </div>
  );
}

// ─── Product bubble card ──────────────────────────────────────────────────
function ProductCard({ product, accent }) {
  const price = fmtPrice(product.price_cents, product.currency);
  const isGold = accent === 'gold';
  return (
    <div
      className="group relative rounded-3xl overflow-hidden border-[3px] transition-transform hover:-translate-y-1 focus-within:-translate-y-1"
      style={{
        background: 'rgba(255,255,255,0.06)',
        borderColor: isGold ? '#ffd900' : 'rgba(255,255,255,0.25)',
        boxShadow: isGold
          ? '0 8px 0 rgba(0,0,0,0.5), 0 0 30px #ffd90055'
          : '0 6px 0 rgba(0,0,0,0.45)',
        backdropFilter: 'blur(6px)',
      }}
      data-testid={`merch-card-${product.id}`}
    >
      {/* Image area — square-ish; falls back to a gradient tile if empty */}
      <div className="relative aspect-square bg-gradient-to-br from-[#3a1064] to-[#14002e]">
        {product.image ? (
          <img
            src={product.image}
            alt={product.title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <ShoppingBag className="h-14 w-14 text-white/30" />
          </div>
        )}
        {/* Category chip */}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/50 text-white text-[9px] font-black uppercase tracking-widest border border-white/20">
          {product.category || 'other'}
        </div>
        {product.featured && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#ffd900] text-[#0a0018] text-[9px] font-black uppercase tracking-widest border-2 border-[#0a0018]">
            HOT
          </div>
        )}
        {!product.in_stock && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <div className="rounded-full bg-[#ff2f6d] text-white px-3 py-1 font-black tracking-wider text-xs border-2 border-[#0a0018]">
              SOLD OUT
            </div>
          </div>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <h3 className="font-black text-white text-sm sm:text-base leading-tight line-clamp-2 min-h-[2.5em]">
          {product.title}
        </h3>
        <div className="mt-2 flex items-center justify-between gap-2">
          {price ? (
            <div
              className="rounded-full bg-[#ffd900] text-[#0a0018] font-black px-3 py-1 text-sm border-2 border-[#0a0018] shadow-[0_2px_0_#0a0018]"
              data-testid={`merch-price-${product.id}`}
            >
              {price}
            </div>
          ) : (
            <div className="text-white/60 text-xs font-bold">See site for price</div>
          )}
          <BuyButton product={product} />
        </div>
      </div>
    </div>
  );
}

function BuyButton({ product }) {
  const isAffiliate = product.inventory_type !== 'stripe';
  const disabled = !product.in_stock;
  if (isAffiliate) {
    return (
      <a
        href={product.external_url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={disabled}
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wider border-2 transition-all ${
          disabled
            ? 'bg-white/10 text-white/40 border-white/20 pointer-events-none'
            : 'bg-[#ff2bd6] text-white border-[#0a0018] shadow-[0_3px_0_#0a0018] hover:bg-[#e119bf]'
        }`}
        data-testid={`merch-buy-${product.id}`}
      >
        Buy <ExternalLink className="h-3 w-3" />
      </a>
    );
  }
  // Stripe path — placeholder until in-app checkout is wired
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => window.alert('In-app checkout coming soon. Contact us to order.')}
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-wider border-2 transition-all ${
        disabled
          ? 'bg-white/10 text-white/40 border-white/20 cursor-not-allowed'
          : 'bg-[#2ee6c1] text-[#0a0018] border-[#0a0018] shadow-[0_3px_0_#0a0018] hover:brightness-95'
      }`}
      data-testid={`merch-buy-${product.id}`}
    >
      Order
    </button>
  );
}

function EmptyState({ active }) {
  return (
    <div className="py-16 text-center">
      <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
        <ShoppingBag className="h-9 w-9 text-white/70" />
      </div>
      <div className="font-black uppercase tracking-widest text-white text-lg">
        {active === 'all'
          ? "No merch yet — check back soon"
          : "Nothing in this category yet"}
      </div>
      <div className="mt-2 text-white/60 text-sm">
        Admins can add products through the Merch tab in the Admin Panel.
      </div>
    </div>
  );
}

export { CATEGORIES as MERCH_CATEGORIES };
